<template>
  <div
    class="relative m-auto w-[1145px] min-w-[700px] max-w-[98vw] rounded-xl bg-gray-900"
  >
    <TButton
      class="!absolute right-[8px] top-[8px]"
      @click="closeTemplatesPicker"
    >
      <template #icon>
        <CloseLarge />
      </template>
    </TButton>

    <div class="flex items-center gap-3 px-5 py-5">
      <div
        class="box-border grid h-[38px] w-[38px] items-center justify-center rounded-md border border-solid border-gray-500 bg-gray-700 text-gray-50"
      >
        <font-awesome-icon :icon="['fas', 'scroll']" />
      </div>
      <div class="text-md font-regular font-inter leading-4 text-gray-100">
        <h1 class="text-base font-semibold text-gray-50">
          {{ $t("blocks.premade_templates") }}
        </h1>
      </div>
    </div>

    <div class="flex items-center justify-between px-5 pb-2">
      <div class="flex gap-2">
        <TDropdown
          v-if="!hideCategoriesDropdown"
          ref="dropdowncategoriesRef"
          data-testid="categories-dropdown"
          class="w-[260px]"
          rounded-full
        >
          <template #selected-content>
            <p v-if="selectedCategories.length === 0" class="!text-gray-100">
              {{ $t("premade_templates.select_categories") }}
            </p>
            <p v-else>
              <span
                v-for="(selectCategory, k) in selectedCategories"
                :key="k"
                class="text-gray-100"
                >{{ k > 0 ? ", " : ""
                }}{{
                  categories.find((item) => item.id === selectCategory)?.name
                }}</span
              >
            </p>
          </template>

          <template #content>
            <ul>
              <VueCustomScrollbar
                style="position: relative; margin: auto"
                :style="{ height: '240px' }"
                :settings="{ wheelPropagation: false }"
              >
                <li
                  v-for="category in categories"
                  :key="category.id"
                  class="group/cat"
                  style="
                    padding: 6px 10px;
                    display: flex;
                    justify-content: flex-start;
                    height: auto;
                  "
                >
                  <div class="flex w-full items-center gap-3">
                    <div class="relative h-[20px]">
                      <input
                        :id="`cat_${category.id}`"
                        v-model="selectedCategories"
                        :value="category.id"
                        class="focus-outline checked:bg-light-accent-1 focus:outline-light-accent-1 checked:!bg-gray-accent-1 checked:bg-gray-accent-1 focus:outline-gray-accent-1 m-0 h-[20px] w-[20px] cursor-pointer appearance-none rounded-md bg-gray-400 transition duration-300 checked:!outline-none focus:outline-2 disabled:!cursor-not-allowed disabled:!bg-gray-600 group-hover/cat:bg-gray-600"
                        type="checkbox"
                      />
                      <div v-show="selectedCategories.includes(category.id)">
                        <svg
                          class="group-hover/cat:text-light-900 group-fous-within:text-light-900 pointer-events-none absolute left-0 top-0 text-white dark:group-focus-within/cat:text-white dark:group-hover:text-white"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8.44801 14C8.19166 14 7.94348 13.8998 7.7597 13.7179L5.28515 11.2685C4.90495 10.8921 4.90495 10.2818 5.28515 9.90546C5.66535 9.52905 6.28187 9.52905 6.66207 9.90546L8.26807 11.4951C9.25894 10.0043 11.2482 7.35156 13.5642 6.1156C14.0376 5.86304 14.6281 6.03809 14.8832 6.5066C15.1384 6.97514 14.9615 7.55974 14.4883 7.81231C11.737 9.2805 9.31979 13.4687 9.2956 13.5108C9.14586 13.7728 8.88074 13.9506 8.57941 13.9912C8.5355 13.9971 8.4916 14 8.44801 14Z"
                            fill="currentColor"
                          />
                        </svg>
                      </div>
                    </div>
                    <label
                      :for="`cat_${category.id}`"
                      class="text-light-200 group-focus-within/cat:!text-light-100 group-hover/cat:text-light-100 block flex-1 cursor-pointer font-inter text-xs font-medium leading-5 transition duration-300 dark:text-gray-100 dark:group-focus-within/cat:!text-gray-100 dark:group-hover/cat:text-gray-100"
                    >
                      {{ category.name }}
                    </label>
                  </div>
                </li>
              </VueCustomScrollbar>
            </ul>
          </template>
        </TDropdown>

        <TDropdown
          v-if="!hideKeywordsDropdown"
          ref="dropdownKeywordsRef"
          data-testid="keywords-dropdown"
          class="w-[260px]"
          rounded-full
        >
          <template #selected-content>
            <p v-if="selectedKeywords.length === 0" class="!text-gray-100">
              {{ $t("premade_templates.select_keywords") }}
            </p>
            <p v-else>
              <span
                v-for="(selectKeyword, k) in selectedKeywords"
                :key="k"
                class="text-gray-100"
                >{{ k > 0 ? ", " : ""
                }}{{
                  keywords.find((item) => item.id === selectKeyword)?.name
                }}</span
              >
            </p>
          </template>

          <template #content>
            <ul>
              <VueCustomScrollbar
                style="position: relative; margin: auto"
                :style="{ height: '240px' }"
                :settings="{ wheelPropagation: false }"
              >
                <li
                  v-for="keyword in keywords"
                  :key="keyword.id"
                  class="group/key"
                  style="
                    padding: 6px 10px;
                    display: flex;
                    justify-content: flex-start;
                    height: auto;
                  "
                >
                  <div class="flex w-full items-center gap-3">
                    <div class="relative h-[20px]">
                      <input
                        :id="`key_${keyword.id}`"
                        v-model="selectedKeywords"
                        :value="keyword.id"
                        class="focus-outline checked:!bg-light-accent-1 checked:bg-light-accent-1 focus:bg-light-600 focus:outline-light-accent-1 disabled:!bg-light-600 group-hover/key:bg-light-600 checked:!bg-gray-accent-1 checked:bg-gray-accent-1 focus:outline-gray-accent-1 m-0 h-[20px] w-[20px] cursor-pointer appearance-none rounded-md bg-gray-400 transition duration-300 checked:!outline-none focus:outline-2 disabled:!cursor-not-allowed disabled:!bg-gray-600 group-hover/key:bg-gray-600"
                        type="checkbox"
                      />
                      <div v-show="selectedKeywords.includes(keyword.id)">
                        <svg
                          class="group-hover/key:text-light-900 group-fous-within/key:text-light-900 pointer-events-none absolute left-0 top-0 text-white dark:group-focus-within/key:text-white dark:group-hover/key:text-white"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8.44801 14C8.19166 14 7.94348 13.8998 7.7597 13.7179L5.28515 11.2685C4.90495 10.8921 4.90495 10.2818 5.28515 9.90546C5.66535 9.52905 6.28187 9.52905 6.66207 9.90546L8.26807 11.4951C9.25894 10.0043 11.2482 7.35156 13.5642 6.1156C14.0376 5.86304 14.6281 6.03809 14.8832 6.5066C15.1384 6.97514 14.9615 7.55974 14.4883 7.81231C11.737 9.2805 9.31979 13.4687 9.2956 13.5108C9.14586 13.7728 8.88074 13.9506 8.57941 13.9912C8.5355 13.9971 8.4916 14 8.44801 14Z"
                            fill="currentColor"
                          />
                        </svg>
                      </div>
                    </div>
                    <label
                      :for="`key_${keyword.id}`"
                      class="text-light-200 group-focus-within/key:!text-light-100 group-hover/key:text-light-100 block flex-1 cursor-pointer font-inter text-xs font-medium leading-5 transition duration-300 dark:text-gray-100 dark:group-focus-within/key:!text-gray-100 dark:group-hover/key:text-gray-100"
                    >
                      {{ keyword.name }}
                    </label>
                  </div>
                </li>
              </VueCustomScrollbar>
            </ul>
          </template>
        </TDropdown>
      </div>

      <div v-if="!hideSearchInput" class="w-[235px]">
        <t-input
          v-model="query"
          data-testid="search"
          :placeholder="$t('premade_templates.search')"
        ></t-input>
      </div>
    </div>

    <div
      class="relative rounded-b-xl border-0 border-t border-solid border-gray-500 bg-gray-700 px-5 py-4"
      style="height: calc(100vh - 180px)"
    >
      <PerfectScrollbar v-if="!loading" style="height: calc(100vh - 180px)">
        <div
          class="grid grid-cols-5 gap-6"
          data-testid="premade-templates-list"
        >
          <div v-for="template in data" :key="template.id" class="center">
            <div
              class="group relative m-0 block cursor-zoom-in appearance-none bg-transparent p-0 pb-[120%] duration-200 ease-in"
              @click="selectTemplate(template.id)"
            >
              <img
                :src="template.img_thumb_url"
                class="absolute h-full w-full object-cover object-top transition-all duration-150 ease-in group-hover:opacity-90"
              />

              <div
                class="absolute bottom-5 hidden w-full justify-center group-hover:inline-flex"
              >
                <button
                  class="cursor-zoom-in rounded-md bg-structure-1 px-3 py-2 font-medium text-white transition-all duration-150 ease-in hover:shadow-md"
                >
                  {{ $t("premade_templates.preview_template") }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <t-button
          v-if="currentPage < lastPage"
          success
          :loading="loadingMore"
          class="mx-auto mt-4"
          @click="loadMore"
        >
          {{ $t("premade_templates.load_more") }}
        </t-button>
      </PerfectScrollbar>
      <div v-else style="padding-top: 16rem">
        <t-loading data-testid="loading-templates" large></t-loading>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import premadeTemplates from "@/composables/usePremadeTemplates";
  import { usePremadeTemplatesStore } from "@/stores/premadeTemplatesStore";
  import {
    Template,
    CategoryAndKeyword,
  } from "types/PremadeTemplates/PremadeTemplates";
  import { api } from "@/api";
  import { CloseLarge } from "@topol/icons";
  import { TLoading, TButton, TDropdown, TInput } from "@topol/twig-ui";
  import { PerfectScrollbar as VueCustomScrollbar } from "vue3-perfect-scrollbar";
  import { watchDebounced } from "@vueuse/core";
  import { ref, Ref, onMounted, computed } from "vue";

  const { getData, getCategories, getKeywords } = premadeTemplates();

  const emits = defineEmits(["closeTemplatesPicker", "selectTemplate"]);

  const data: Ref<Template[]> = ref([]);
  const categories: Ref<CategoryAndKeyword[] | []> = ref([]);
  const keywords: Ref<CategoryAndKeyword[] | []> = ref([]);
  const loading = ref(true);
  const loadingMore = ref(false);
  const currentPage = ref(1);
  const pageLength = ref(25);
  const lastPage = ref(1);
  const categoryQueryString = ref("");
  const keywordsQueryString = ref("");

  const premadeTemplatesStore = usePremadeTemplatesStore();

  const hideCategoriesDropdown = computed(() => {
    return !api.PREMADE_TEMPLATE_CATEGORIES;
  });

  const hideKeywordsDropdown = computed(() => {
    return !api.PREMADE_TEMPLATES_KEYWORDS;
  });

  const hideSearchInput = computed(() => {
    return premadeTemplatesStore.hideSearch;
  });

  const selectedCategories = computed({
    get() {
      return premadeTemplatesStore.selectedCategories;
    },
    set(val: number[]) {
      premadeTemplatesStore.selectedCategories = val;
    },
  });

  const selectedKeywords = computed({
    get() {
      return premadeTemplatesStore.selectedKeywords;
    },
    set(val: number[]) {
      premadeTemplatesStore.selectedKeywords = val;
    },
  });

  const query = computed({
    get() {
      return premadeTemplatesStore.query;
    },
    set(val: string) {
      premadeTemplatesStore.query = val;
    },
  });

  const closeTemplatesPicker = () => {
    emits("closeTemplatesPicker");
  };

  const selectTemplate = (id: number) => {
    emits("selectTemplate", id);
  };

  watchDebounced(
    [query, selectedCategories, selectedKeywords],
    async (newQuery) => {
      loading.value = true;
      currentPage.value = 1;
      categoryQueryString.value = "";
      keywordsQueryString.value = "";

      for (const category of newQuery[1]) {
        categoryQueryString.value += `&categories[]=${category}`;
      }

      for (const keyword of newQuery[2]) {
        keywordsQueryString.value += `&keywords[]=${keyword}`;
      }

      const response = await getData(
        1,
        newQuery[0],
        pageLength.value,
        newQuery[1],
        newQuery[2]
      );

      if (response) {
        data.value = response.data;
        lastPage.value = response.last_page;
      }

      loading.value = false;
    },
    { debounce: 350 }
  );

  async function loadMore() {
    loadingMore.value = true;
    currentPage.value++;

    const response = await getData(
      currentPage.value,
      query.value,
      pageLength.value,
      selectedCategories.value,
      selectedKeywords.value
    );

    if (response && response.data) {
      data.value = data.value.concat(response.data);
    }

    loadingMore.value = false;
  }

  onMounted(async () => {
    const response = await getData(
      1,
      query.value,
      pageLength.value,
      selectedCategories.value,
      selectedKeywords.value
    );

    if (response) {
      data.value = response.data;
      lastPage.value = response.last_page;
    }

    if (!hideCategoriesDropdown.value) {
      categories.value = await getCategories();
    }

    if (!hideKeywordsDropdown.value) {
      keywords.value = await getKeywords();
    }

    loading.value = false;
  });
</script>
